// =============================================================================
// merchantNames.js — Canonical display-name resolver for transaction text
// =============================================================================
//
// Why this exists (Phase 70B — Trust polish):
//
//   Bank statements ship transaction descriptions that are a mix of human
//   intent and machine routing strings:
//
//     "UPI-NETFLIX ENTERTAINMENT-NETFLIX@AXISB-AXIS-XYZ-123"
//     "POS PUR WHOLEFDSMRKT 10453 NEW YORK USA"
//     "ACH DEBIT VANGUARD MARKETING CORP 0000123"
//     "UPI-SWIGGY-SWIGGY@OKICICI-ICIC0000001"
//
//   Showing these raw makes 2120 feel like a bank app, not a clarity
//   app. We want users to see "Netflix", "Whole Foods", "Vanguard",
//   "Swiggy" while keeping the raw string available for search,
//   debugging, and audit.
//
// Design:
//
//   • Pure JS — no DB lookup, no async, no network. Resolves at render
//     time everywhere a transaction description is shown. Cheap enough
//     to call on every row.
//   • Patterns are lowercase + alphanumeric-only after normalisation, so
//     "UPI-NETFLIX-XYZ" → "upinetflixxyz" → matches the pattern "netflix"
//     as a substring.
//   • First match wins. The seed list is ordered roughly by specificity
//     (longer/more-distinctive patterns first) so "amazon prime" beats
//     "amazon".
//   • Always returns SOMETHING — if no alias matches, the original
//     (titlecased, with bank-prefix garbage trimmed) is returned so the
//     UI never looks broken.
//   • Raw description is NEVER mutated server-side. This module is
//     display-only. Search/match logic continues to work on raw text.
//
// Extensibility:
//
//   For Phase A (auto-detect recurring), the detector also uses
//   `canonicalKey(raw)` to group transactions into "streams" (e.g., all
//   Netflix charges in the last 6 months are one stream even if the
//   suffix changes monthly).
//
//   In the future, this static seed can be augmented or overridden by a
//   `merchant_aliases` DB table per workspace. The resolver signature
//   takes an optional `extraAliases` array exactly for that purpose.
// =============================================================================

/**
 * Normalise raw text to a comparable form. Lowercase, strip all
 * non-alphanumeric characters, collapse whitespace. Used as the
 * comparison key for pattern matching.
 */
export function normaliseRaw(raw) {
  return String(raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * Stable "stream key" for grouping transactions by what they're really
 * for, regardless of surrounding bank/UPI noise. Used by the auto-detect
 * detector — same canonical name maps to the same key.
 */
export function canonicalKey(raw, extraAliases = []) {
  const display = resolveMerchantName(raw, extraAliases);
  return normaliseRaw(display);
}

/**
 * Built-in seed list. Pattern is matched as a substring against the
 * normalised raw text. Longer/more-specific patterns must appear first.
 *
 * ~100 entries covering:
 *   • India-first: UPI merchants, Indian fintech, FMCG, food delivery
 *   • Global SaaS most users have: Netflix, Spotify, AWS, etc.
 *   • US grocery / retail (for Mint refugees with US history)
 *   • UAE / GCC (your origin market)
 *
 * Categories are optional and are used as a default when the resolver
 * is also asked to suggest a category. Leave undefined to let the
 * existing category-rules engine handle it.
 */
const SEED_ALIASES = [
  // --- Streaming / digital subscriptions ---------------------------
  { pattern: 'amazonprimevideo', display: 'Amazon Prime Video', category: 'Subscriptions' },
  { pattern: 'amazonprime',      display: 'Amazon Prime',       category: 'Subscriptions' },
  { pattern: 'netflix',          display: 'Netflix',            category: 'Subscriptions' },
  { pattern: 'spotify',          display: 'Spotify',            category: 'Subscriptions' },
  { pattern: 'youtubepremium',   display: 'YouTube Premium',    category: 'Subscriptions' },
  { pattern: 'appleitunes',      display: 'Apple iTunes',       category: 'Subscriptions' },
  { pattern: 'applecom',         display: 'Apple',              category: 'Subscriptions' },
  { pattern: 'applepay',         display: 'Apple Pay',          category: 'Subscriptions' },
  // NOTE: pattern 'apple' alone is deliberately omitted — it would
  // false-match "PINEAPPLE", "APPLEBEE'S", etc.
  { pattern: 'icloud',           display: 'iCloud',             category: 'Subscriptions' },
  { pattern: 'disneyplus',       display: 'Disney+',            category: 'Subscriptions' },
  { pattern: 'hotstar',          display: 'Disney+ Hotstar',    category: 'Subscriptions' },
  { pattern: 'sonyliv',          display: 'SonyLIV',            category: 'Subscriptions' },
  { pattern: 'zee5',             display: 'ZEE5',               category: 'Subscriptions' },
  { pattern: 'jiocinema',        display: 'JioCinema',          category: 'Subscriptions' },
  { pattern: 'jiosaavn',         display: 'JioSaavn',           category: 'Subscriptions' },
  { pattern: 'gaana',            display: 'Gaana',              category: 'Subscriptions' },
  { pattern: 'audible',          display: 'Audible',            category: 'Subscriptions' },
  { pattern: 'kindle',           display: 'Amazon Kindle',      category: 'Subscriptions' },
  { pattern: 'chatgpt',          display: 'ChatGPT',            category: 'Subscriptions' },
  { pattern: 'openai',           display: 'OpenAI',             category: 'Subscriptions' },
  { pattern: 'anthropic',        display: 'Anthropic',          category: 'Subscriptions' },
  { pattern: 'claudeai',         display: 'Claude AI',          category: 'Subscriptions' },
  { pattern: 'notion',           display: 'Notion',             category: 'Subscriptions' },
  { pattern: 'figma',            display: 'Figma',              category: 'Subscriptions' },
  { pattern: 'github',           display: 'GitHub',             category: 'Subscriptions' },
  { pattern: 'googlecloud',      display: 'Google Cloud',       category: 'Subscriptions' },
  { pattern: 'googleworkspace',  display: 'Google Workspace',   category: 'Subscriptions' },
  { pattern: 'googleplay',       display: 'Google Play',        category: 'Subscriptions' },
  { pattern: 'awsamazon',        display: 'Amazon Web Services',category: 'Subscriptions' },
  { pattern: 'awsmarketplace',   display: 'AWS Marketplace',    category: 'Subscriptions' },
  { pattern: 'microsoft365',     display: 'Microsoft 365',      category: 'Subscriptions' },
  { pattern: 'microsoft',        display: 'Microsoft',          category: 'Subscriptions' },
  { pattern: 'dropbox',          display: 'Dropbox',            category: 'Subscriptions' },

  // --- Food delivery & dining (India + global) ---------------------
  { pattern: 'swiggyinstamart',  display: 'Swiggy Instamart',   category: 'Groceries' },
  { pattern: 'swiggygenie',      display: 'Swiggy Genie',       category: 'Food & Dining' },
  { pattern: 'swiggy',           display: 'Swiggy',             category: 'Food & Dining' },
  { pattern: 'zomatogold',       display: 'Zomato Gold',        category: 'Food & Dining' },
  { pattern: 'zomato',           display: 'Zomato',             category: 'Food & Dining' },
  { pattern: 'eatfit',           display: 'EatFit',             category: 'Food & Dining' },
  { pattern: 'doordash',         display: 'DoorDash',           category: 'Food & Dining' },
  { pattern: 'ubereats',         display: 'Uber Eats',          category: 'Food & Dining' },
  { pattern: 'grubhub',          display: 'Grubhub',            category: 'Food & Dining' },
  { pattern: 'talabat',          display: 'Talabat',            category: 'Food & Dining' },
  { pattern: 'careemfood',       display: 'Careem Food',        category: 'Food & Dining' },
  { pattern: 'starbucks',        display: 'Starbucks',          category: 'Food & Dining' },
  { pattern: 'mcdonalds',        display: 'McDonald’s',    category: 'Food & Dining' },
  { pattern: 'dominos',          display: 'Domino’s',      category: 'Food & Dining' },
  { pattern: 'kfc outlet',       display: 'KFC',                category: 'Food & Dining' },
  { pattern: 'kentucky fried',   display: 'KFC',                category: 'Food & Dining' },
  // NOTE: 'kfc' alone is acceptable but we prefer the longer-anchored
  // patterns above so we don't pick up "TFKFC" or random sequences.
  { pattern: 'subway',           display: 'Subway',             category: 'Food & Dining' },
  // Phase 84.16 \u2014 Subway India's operator entity is "Eversub India Pvt Ltd";
  // some bank statements print it verbatim. Route to Subway so the row
  // lands in Food & Dining (previously the LLM was substring-matching
  // "sub" \u2192 "Subscriptions", a semantic false positive).
  { pattern: 'eversub',          display: 'Subway',             category: 'Food & Dining' },
  { pattern: 'thirdwavecoffee',  display: 'Third Wave Coffee',  category: 'Food & Dining' },
  { pattern: 'bluetokai',        display: 'Blue Tokai',         category: 'Food & Dining' },

  // --- Grocery / FMCG ----------------------------------------------
  { pattern: 'blinkit',          display: 'Blinkit',            category: 'Groceries' },
  { pattern: 'zeptonow',         display: 'Zepto',              category: 'Groceries' },
  { pattern: 'zepto',            display: 'Zepto',              category: 'Groceries' },
  { pattern: 'bigbasket',        display: 'BigBasket',          category: 'Groceries' },
  { pattern: 'dmart',            display: 'DMart',              category: 'Groceries' },
  { pattern: 'reliancefresh',    display: 'Reliance Fresh',     category: 'Groceries' },
  { pattern: 'reliancesmart',    display: 'Reliance Smart',     category: 'Groceries' },
  { pattern: 'naturesbasket',    display: 'Nature’s Basket', category: 'Groceries' },
  { pattern: 'wholefdsmrkt',     display: 'Whole Foods',        category: 'Groceries' },
  { pattern: 'wholefoods',       display: 'Whole Foods',        category: 'Groceries' },
  { pattern: 'traderjoes',       display: 'Trader Joe’s',  category: 'Groceries' },
  { pattern: 'safeway',          display: 'Safeway',            category: 'Groceries' },
  { pattern: 'costco',           display: 'Costco',             category: 'Groceries' },
  { pattern: 'carrefour',        display: 'Carrefour',          category: 'Groceries' },
  { pattern: 'lulu',             display: 'Lulu Hypermarket',   category: 'Groceries' },

  // --- E-commerce / retail -----------------------------------------
  { pattern: 'amazonin',         display: 'Amazon',             category: 'Shopping' },
  { pattern: 'amazoncom',        display: 'Amazon',             category: 'Shopping' },
  { pattern: 'amazonpay',        display: 'Amazon Pay',         category: 'Shopping' },
  { pattern: 'amazon',           display: 'Amazon',             category: 'Shopping' },
  { pattern: 'flipkart',         display: 'Flipkart',           category: 'Shopping' },
  { pattern: 'myntra',           display: 'Myntra',             category: 'Shopping' },
  { pattern: 'ajio',             display: 'AJIO',               category: 'Shopping' },
  { pattern: 'nykaa',            display: 'Nykaa',              category: 'Shopping' },
  { pattern: 'meeshofresh',      display: 'Meesho',             category: 'Shopping' },
  { pattern: 'meesho',           display: 'Meesho',             category: 'Shopping' },
  { pattern: 'ikea',             display: 'IKEA',               category: 'Shopping' },
  { pattern: 'sheinme',          display: 'Shein',              category: 'Shopping' },
  { pattern: 'shein com',        display: 'Shein',              category: 'Shopping' },
  { pattern: 'lazadasg',         display: 'Lazada',             category: 'Shopping' },
  { pattern: 'noon ae',          display: 'Noon',               category: 'Shopping' },
  { pattern: 'noonshop',         display: 'Noon',               category: 'Shopping' },
  // NOTE: 'noon' alone is omitted — would false-match "AFTERNOON".

  // --- Transport / ride-hailing ------------------------------------
  { pattern: 'uber india',       display: 'Uber',               category: 'Transportation' },
  { pattern: 'uberin',           display: 'Uber',               category: 'Transportation' },
  { pattern: 'uber bv',          display: 'Uber',               category: 'Transportation' },
  { pattern: 'uber pay',         display: 'Uber',               category: 'Transportation' },
  { pattern: 'uberride',         display: 'Uber',               category: 'Transportation' },
  // NOTE: 'uber' alone is omitted — would false-match "TUBER",
  // "RUBBER", etc. Same rationale for 'ola'.
  { pattern: 'ola cabs',         display: 'Ola',                category: 'Transportation' },
  { pattern: 'olamoney',         display: 'Ola Money',          category: 'Transportation' },
  { pattern: 'olapay',           display: 'Ola',                category: 'Transportation' },
  { pattern: 'olain',            display: 'Ola',                category: 'Transportation' },
  { pattern: 'rapido',           display: 'Rapido',             category: 'Transportation' },
  { pattern: 'namma yatri',      display: 'Namma Yatri',        category: 'Transportation' },
  { pattern: 'careem',           display: 'Careem',             category: 'Transportation' },
  { pattern: 'lyft',             display: 'Lyft',               category: 'Transportation' },
  { pattern: 'irctc',            display: 'IRCTC',              category: 'Transportation' },
  { pattern: 'redbus',           display: 'RedBus',             category: 'Transportation' },
  { pattern: 'makemytrip',       display: 'MakeMyTrip',         category: 'Travel' },
  { pattern: 'goibibo',          display: 'Goibibo',            category: 'Travel' },
  { pattern: 'cleartrip',        display: 'Cleartrip',          category: 'Travel' },
  { pattern: 'easemytrip',       display: 'EaseMyTrip',         category: 'Travel' },
  { pattern: 'indigo6e',         display: 'IndiGo',             category: 'Travel' },
  { pattern: 'vistara',          display: 'Vistara',            category: 'Travel' },
  { pattern: 'airindia',         display: 'Air India',          category: 'Travel' },
  { pattern: 'emirates',         display: 'Emirates',           category: 'Travel' },
  { pattern: 'qatarairways',     display: 'Qatar Airways',      category: 'Travel' },
  { pattern: 'singaporeair',     display: 'Singapore Airlines', category: 'Travel' },
  { pattern: 'booking',          display: 'Booking.com',        category: 'Travel' },
  { pattern: 'airbnb',           display: 'Airbnb',             category: 'Travel' },

  // --- Utilities ----------------------------------------------------
  { pattern: 'airtel',           display: 'Airtel',             category: 'Utilities' },
  { pattern: 'reliancejio',      display: 'Jio',                category: 'Utilities' },
  { pattern: 'jiofiber',         display: 'JioFiber',           category: 'Utilities' },
  { pattern: 'vodafoneidea',     display: 'Vi (Vodafone Idea)', category: 'Utilities' },
  { pattern: 'bsnl',             display: 'BSNL',               category: 'Utilities' },
  { pattern: 'tatasky',          display: 'Tata Play',          category: 'Utilities' },
  { pattern: 'tatapower',        display: 'Tata Power',         category: 'Utilities' },
  { pattern: 'adanielectricity', display: 'Adani Electricity',  category: 'Utilities' },
  { pattern: 'mahanagargas',     display: 'Mahanagar Gas',      category: 'Utilities' },
  { pattern: 'indraprastha',     display: 'Indraprastha Gas',   category: 'Utilities' },
  { pattern: 'duelectric',       display: 'DEWA',               category: 'Utilities' },
  { pattern: 'verizon',          display: 'Verizon',            category: 'Utilities' },
  { pattern: 'tmobile',          display: 'T-Mobile',           category: 'Utilities' },
  // NOTE: 'att' is deliberately omitted as a top-level seed to avoid
  // false-matching words like "ATTACHMENT", "MATTRESS", etc. Users
  // with AT&T bills can rely on the fallback prettifier instead.
  { pattern: 'comcastxfin',      display: 'Xfinity',            category: 'Utilities' },
  { pattern: 'xfinity',          display: 'Xfinity',            category: 'Utilities' },

  // --- Investment / brokerage / wealth -----------------------------
  { pattern: 'zerodha',          display: 'Zerodha',            category: 'Investments' },
  { pattern: 'groww',            display: 'Groww',              category: 'Investments' },
  { pattern: 'upstox',           display: 'Upstox',             category: 'Investments' },
  { pattern: 'kuvera',           display: 'Kuvera',             category: 'Investments' },
  { pattern: 'indianclearing',   display: 'Mutual Fund SIP',    category: 'Investments' },
  { pattern: 'mfucan',           display: 'Mutual Fund',        category: 'Investments' },
  { pattern: 'bsestar',          display: 'BSE StAR MF',        category: 'Investments' },
  { pattern: 'icicidirect',      display: 'ICICI Direct',       category: 'Investments' },
  { pattern: 'hdfcsecurities',   display: 'HDFC Securities',    category: 'Investments' },
  { pattern: 'kotaksecurities',  display: 'Kotak Securities',   category: 'Investments' },
  { pattern: 'vanguard',         display: 'Vanguard',           category: 'Investments' },
  { pattern: 'fidelity',         display: 'Fidelity',           category: 'Investments' },
  { pattern: 'charlesschwab',    display: 'Charles Schwab',     category: 'Investments' },
  { pattern: 'robinhood',        display: 'Robinhood',          category: 'Investments' },
  { pattern: 'wealthfront',      display: 'Wealthfront',        category: 'Investments' },
  { pattern: 'coinbase',         display: 'Coinbase',           category: 'Investments' },
  { pattern: 'binance',          display: 'Binance',            category: 'Investments' },
  { pattern: 'wazirx',           display: 'WazirX',             category: 'Investments' },
  { pattern: 'coindcx',          display: 'CoinDCX',            category: 'Investments' },

  // --- Insurance ----------------------------------------------------
  { pattern: 'liclifeinsurance', display: 'LIC',                category: 'Insurance' },
  { pattern: 'licpremium',       display: 'LIC',                category: 'Insurance' },
  // NOTE: 'lic' alone is deliberately omitted — it would match
  // "LICENSE", "LICENCE", etc. We require a 2nd discriminating token.
  { pattern: 'hdfclife',         display: 'HDFC Life',          category: 'Insurance' },
  { pattern: 'iciciprulife',     display: 'ICICI Pru Life',     category: 'Insurance' },
  { pattern: 'maxlifeinsurance', display: 'Max Life',           category: 'Insurance' },
  { pattern: 'starhealth',       display: 'Star Health',        category: 'Insurance' },
  { pattern: 'acko',             display: 'Acko',               category: 'Insurance' },
  { pattern: 'digitinsurance',   display: 'Go Digit',           category: 'Insurance' },

  // --- Banking / fintech rails -------------------------------------
  { pattern: 'paytm',            display: 'Paytm',              category: 'Miscellaneous' },
  { pattern: 'phonepe',          display: 'PhonePe',            category: 'Miscellaneous' },
  { pattern: 'cred club',        display: 'CRED',               category: 'Miscellaneous' },
  { pattern: 'credpay',          display: 'CRED Pay',           category: 'Miscellaneous' },
  // NOTE: 'cred' alone is deliberately omitted — it would match
  // "CREDIT", "CREDENTIAL", etc.
  { pattern: 'razorpay',         display: 'Razorpay',           category: 'Miscellaneous' },
  { pattern: 'stripe',           display: 'Stripe',             category: 'Miscellaneous' },
  { pattern: 'paypal',           display: 'PayPal',             category: 'Miscellaneous' },
  { pattern: 'wise',             display: 'Wise',               category: 'Miscellaneous' },
  { pattern: 'cashapp',          display: 'Cash App',           category: 'Miscellaneous' },
  { pattern: 'venmo',            display: 'Venmo',              category: 'Miscellaneous' },
];

// Pre-normalise the seed patterns ONCE at module load. The hot path
// (every transaction-row render) becomes O(N) substring scans on a
// pre-normalised needle.
const COMPILED = SEED_ALIASES.map((a) => ({
  needle: normaliseRaw(a.pattern),
  display: a.display,
  category: a.category,
}));

/**
 * Resolve the canonical display name for a raw transaction description.
 * Returns `{ display, category }`. `category` may be undefined; callers
 * should treat that as "I don't have an opinion, defer to your rules".
 *
 * If nothing matches, falls back to a lightly-cleaned title-case of the
 * input (so we never render an empty string).
 *
 * @param {string} raw - Raw transaction description from the bank/CSV.
 * @param {Array} [extraAliases] - Optional override list, same shape as
 *   SEED_ALIASES. Useful for tests or for a future per-workspace DB
 *   table. These are checked BEFORE the built-in seed so they take
 *   precedence.
 */
export function resolveMerchantNameRich(raw, extraAliases = []) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { display: '', category: undefined, hit: false };
  const norm = normaliseRaw(trimmed);
  if (!norm) return { display: trimmed, category: undefined, hit: false };

  // Pre-compile any caller-supplied overrides (cheap; usually empty).
  const overrides = (extraAliases || []).map((a) => ({
    needle: normaliseRaw(a.pattern),
    display: a.display,
    category: a.category,
  }));
  for (const a of overrides) {
    if (a.needle && norm.includes(a.needle)) {
      return { display: a.display, category: a.category, hit: true };
    }
  }
  for (const a of COMPILED) {
    if (a.needle && norm.includes(a.needle)) {
      return { display: a.display, category: a.category, hit: true };
    }
  }
  // `hit: false` means we fell back to the prettifier; callers that
  // care (e.g. the txDescription pipeline) can short-circuit only on
  // genuine seed-hits and let their own parsers run otherwise.
  return { display: prettifyFallback(trimmed), category: undefined, hit: false };
}

/**
 * Convenience wrapper that returns just the display string. The most
 * common call shape from the UI.
 */
export function resolveMerchantName(raw, extraAliases = []) {
  return resolveMerchantNameRich(raw, extraAliases).display;
}

/**
 * When no alias matches we still try to clean up the raw description so
 * the UI doesn't show "POS PUR XYZ 12345 NEW YORK USA" as-is. We:
 *   • Strip leading bank-method tokens (UPI-, POS, ACH DEBIT, ATM, etc.)
 *   • Drop trailing reference numbers, dates, and location codes
 *   • Title-case the rest
 *
 * This is intentionally conservative — we leave the result recognisable.
 * The user can always tap to see the raw string.
 */
function prettifyFallback(raw) {
  let s = String(raw || '');

  // 1) Strip a common set of leading method/channel tokens. Iterative
  // so we trim multiple if they stack (e.g., "POS PUR ").
  const LEADING = /^(upi[-:]?|pos\s*pur|pos\s*purch|pos\b|ach\s*debit|ach\s*credit|atm\s*wd|atm\s*withdrawal|atm\b|imps[-:]?|neft[-:]?|rtgs[-:]?|debit\s*card|credit\s*card|card\s*payment|payment\b)\s*[-:]?\s*/i;
  for (let i = 0; i < 3; i++) {
    const next = s.replace(LEADING, '');
    if (next === s) break;
    s = next;
  }

  // 2) Drop trailing long reference numbers and dates.
  s = s.replace(/\b\d{6,}\b.*$/, '').trim();
  // YYYY-MM-DD or DD/MM/YYYY-style trailing
  s = s.replace(/\b\d{2,4}[-/]\d{1,2}[-/]\d{1,4}\b.*$/, '').trim();

  // 3) Drop UPI VPA suffixes ("name@bank") — keep just the name part.
  const at = s.indexOf('@');
  if (at > 1) s = s.slice(0, at).trim();

  // 4) Collapse runs of separators that bank text loves to use.
  s = s.replace(/[-_/.]+/g, ' ').replace(/\s+/g, ' ').trim();

  // 5) Title-case words longer than 2 chars; leave short ones alone
  // (so "KFC" doesn't become "Kfc").
  s = s.split(' ').map((w) => {
    if (w.length <= 3 && w === w.toUpperCase()) return w; // ACRONYM
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(' ');

  return s || raw;
}

/**
 * Tiny helper for tests / dev — exposes the compiled seed size so
 * regressions in the seed file are visible.
 */
export function _seedSize() { return COMPILED.length; }
